/**
 * US subject-area baselines — aggregate labour-market signals for US
 * bachelor's graduates by broad discipline.
 *
 * Sources cited per entry:
 *   • NACE First Destination Survey 2023 (national aggregate) — employment
 *     rate 6 months after graduation, by CIP-2 major.
 *     https://www.naceweb.org/job-market/graduate-outcomes/
 *   • BLS Occupational Outlook Handbook + Wage Statistics 2024 — median
 *     annual wage & projected 10-year job growth by field.
 *     https://www.bls.gov/ooh/
 *
 * These are national baselines — never per-programme. Framed clearly as
 * "national average, not degree-specific" in the UI.
 */

export const US_SUBJECT_BASELINES = {
  "Engineering": {
    median_starting_salary_usd: 78200,
    median_wage_2024_usd: 100640,
    employment_rate: 0.87,
    growth_10y: 0.06,
    source_name: "NACE 2023 + BLS OOH (Architecture & Engineering group)",
    source_url: "https://www.bls.gov/ooh/architecture-and-engineering/home.htm",
    notes: "STEM-designated — 3yr OPT (12mo + 24mo extension) available.",
  },
  "Computer Science & IT": {
    median_starting_salary_usd: 85100,
    median_wage_2024_usd: 104420,
    employment_rate: 0.90,
    growth_10y: 0.15,
    source_name: "NACE 2023 + BLS OOH (Computer & Information Technology)",
    source_url: "https://www.bls.gov/ooh/computer-and-information-technology/home.htm",
    notes: "Fastest-growing major job group in the US — 15% projected 2022-32.",
  },
  "Business & Law": {
    median_starting_salary_usd: 62700,
    median_wage_2024_usd: 79050,
    employment_rate: 0.83,
    growth_10y: 0.07,
    source_name: "NACE 2023 + BLS OOH (Business & Financial Operations)",
    source_url: "https://www.bls.gov/ooh/business-and-financial/home.htm",
  },
  "Health & Medicine": {
    median_starting_salary_usd: 60400,
    median_wage_2024_usd: 80820,
    employment_rate: 0.91,
    growth_10y: 0.13,
    source_name: "NACE 2023 + BLS OOH (Healthcare Practitioners)",
    source_url: "https://www.bls.gov/ooh/healthcare/home.htm",
    notes: "13% projected growth — much faster than average.",
  },
  "Natural Sciences": {
    median_starting_salary_usd: 51900,
    median_wage_2024_usd: 76690,
    employment_rate: 0.78,
    growth_10y: 0.07,
    source_name: "NACE 2023 + BLS OOH (Life, Physical & Social Sciences)",
    source_url: "https://www.bls.gov/ooh/life-physical-and-social-science/home.htm",
    notes: "Bachelor's often paired with graduate study for higher earning.",
  },
  "Arts & Humanities": {
    median_starting_salary_usd: 45400,
    median_wage_2024_usd: 55240,
    employment_rate: 0.76,
    growth_10y: 0.03,
    source_name: "NACE 2023 + BLS OOH (Arts, Design, Entertainment, Sports & Media)",
    source_url: "https://www.bls.gov/ooh/arts-and-design/home.htm",
    notes: "Wide variance by specialisation; freelance/gig work common.",
  },
  "Social Sciences": {
    median_starting_salary_usd: 51100,
    median_wage_2024_usd: 62240,
    employment_rate: 0.78,
    growth_10y: 0.05,
    source_name: "NACE 2023 + BLS OOH (Community & Social Service)",
    source_url: "https://www.bls.gov/ooh/community-and-social-service/home.htm",
  },
  "Education": {
    median_starting_salary_usd: 44530,
    median_wage_2024_usd: 62310,
    employment_rate: 0.85,
    growth_10y: 0.01,
    source_name: "NACE 2023 + BLS OOH (Education Instruction & Library)",
    source_url: "https://www.bls.gov/ooh/education-training-and-library/home.htm",
    notes: "Public-service pay; unions common. State licensure required.",
  },
  "Agriculture": {
    median_starting_salary_usd: 48200,
    median_wage_2024_usd: 74310,
    employment_rate: 0.79,
    growth_10y: 0.04,
    source_name: "NACE 2023 + BLS OOH (Agriculture & Related)",
    source_url: "https://www.bls.gov/ooh/farming-fishing-and-forestry/home.htm",
  },
  "Other": {
    median_starting_salary_usd: 55000,
    median_wage_2024_usd: 74580,
    employment_rate: 0.82,
    growth_10y: 0.05,
    source_name: "NACE 2023 all-bachelor's national aggregate",
    source_url: "https://www.naceweb.org/",
    notes: "National average across all bachelor's fields.",
  },
};

export function getUsBaseline(subject_area) {
  return US_SUBJECT_BASELINES[subject_area] || US_SUBJECT_BASELINES["Other"];
}
